/**
 * §8.5 회의 중 채팅 — 우하단 아이콘 버튼 + 팝업 패널(§14.2).
 *
 * 설계 의도:
 *  · 자막이 주인공이다. 채팅은 **평소에 접혀 있고** 아이콘 하나만 차지한다 —
 *    상시 노출 사이드바는 좁은 화면에서 자막 폭을 먹는다.
 *  · 이름은 **첫 메시지와 함께** 받는다. 별도 닉네임 입력 단계를 만들면
 *    대화를 시작하려는 사람이 폼을 하나 더 만난다. 첫 전송 때만 이름 칸이
 *    보이고, 그 뒤로는 사라진다(서버가 소켓 단위로 기억한다).
 *  · 읽지 않은 메시지는 아이콘 배지로 알린다 — 패널을 열지 않아도 알 수 있다.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Button } from '@cupia/ui/button'
import { Badge } from '@cupia/ui/badge'
import { Input } from '@cupia/ui/input'
import { MessageSquare, Send, Trash2, X } from 'lucide-react'
import type { ChatItem } from '../lib/reducer'

/** 표시 이름 상한 — 서버(CHAT_NAME_MAX_CHARS)와 같은 값이어야 한다. */
const NAME_MAX = 24
/** 본문 상한 — 서버(CHAT_MAX_CHARS)와 같은 값. 초과분은 서버가 자른다. */
const BODY_MAX = 500

function clock(ts: number): string {
  return new Date(ts).toLocaleTimeString('ko-KR', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function ChatPanel({
  chat,
  onSend,
  onDelete,
  error,
  disabled = false,
}: {
  chat: ChatItem[]
  /** name 은 첫 전송에만 실린다(§14.2) */
  onSend: (body: string, name?: string) => void
  /** 호스트만 삭제할 수 있다(§8.5) — 없으면 삭제 버튼을 그리지 않는다 */
  onDelete?: (item: ChatItem) => void
  /**
   * §8.5 내 입력이 거부된 사유(이름 중복 등) — **입력 옆에 인라인으로**
   * 보여 준다. 방 배너와 섞지 않는다: 이건 내 문제고 고칠 사람도 나뿐이다.
   * 문구는 **서버가 정본**이므로 그대로 싣는다.
   */
  error?: { message: string; ts: number } | null
  disabled?: boolean
}): React.ReactElement {
  const [open, setOpen] = useState(false)
  const [body, setBody] = useState('')
  const [name, setName] = useState('')
  /** 이름을 한 번 보냈으면 더 묻지 않는다(§14.2). */
  const [named, setNamed] = useState(false)

  /**
   * 이름이 거부되면 이름 칸을 **다시 연다**.
   *
   * 거부는 비동기로 온다. 그 사이 이미 named=true 로 칸을 접어 버리면,
   * 사용자는 메시지가 사라진 이유도 모른 채 다른 이름을 고를 방법조차
   * 잃는다(이후 전송은 이름 없이 나가 조용히 "익명n" 이 된다).
   */
  useEffect(() => {
    if (error) setNamed(false)
  }, [error])
  const [seenSeq, setSeenSeq] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)

  const latestSeq = chat.length > 0 ? (chat[chat.length - 1]?.seq ?? 0) : 0
  const unread = chat.filter((c) => c.seq > seenSeq && !c.deleted).length

  // 열려 있으면 읽은 것으로 친다.
  useEffect(() => {
    if (open) setSeenSeq(latestSeq)
  }, [open, latestSeq])

  // 새 메시지가 오면 아래로 붙인다(대화는 시간순이라 최신이 아래다).
  useLayoutEffect(() => {
    if (!open) return
    const el = listRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [open, chat.length])

  const submit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault()
      const text = body.trim()
      if (!text || disabled) return
      // 이름은 첫 전송에만 실어 보낸다 — 이후는 서버가 기억한다.
      onSend(text, named ? undefined : name.trim() || undefined)
      setBody('')
      setNamed(true)
    },
    [body, name, named, onSend, disabled],
  )

  return (
    <>
      {/* 우하단 토글 — 평소에는 이것만 보인다 */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? '대화 닫기' : '대화 열기'}
        aria-expanded={open}
        data-testid="chat-toggle"
        className="fixed bottom-5 right-5 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-primary)] text-[var(--color-primary-foreground)] shadow-lg transition-transform hover:scale-105"
      >
        {open ? <X className="h-5 w-5" /> : <MessageSquare className="h-5 w-5" />}
        {!open && unread > 0 && (
          <span
            data-testid="chat-unread"
            className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--color-destructive)] px-1 text-(length:--text-xs-size) font-semibold text-[var(--color-primary-foreground)]"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <section
          data-testid="chat-panel"
          aria-label="회의 대화"
          className="fixed bottom-20 right-5 z-40 flex h-[min(28rem,60vh)] w-[min(22rem,calc(100vw-2.5rem))] flex-col rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-card)] shadow-xl"
        >
          <header className="flex items-center gap-2 border-b border-[var(--color-border)] px-3 py-2">
            <MessageSquare className="h-4 w-4 text-[var(--color-primary)]" />
            <h2 className="text-(length:--text-sm-size) font-semibold">대화</h2>
            <Badge variant="count">{chat.filter((c) => !c.deleted).length}</Badge>
          </header>

          <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
            {chat.length === 0 ? (
              <p className="py-6 text-center text-(length:--text-xs-size) text-[var(--color-muted-foreground)]">
                아직 대화가 없습니다.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {chat.map((c) => (
                  <li key={c.id} data-testid="chat-message" className="group">
                    {c.deleted ? (
                      // tombstone — 자리는 남기고 내용을 숨긴다(§8.5).
                      <p className="text-(length:--text-xs-size) italic text-[var(--color-muted-foreground)]">
                        삭제된 메시지입니다
                      </p>
                    ) : (
                      <>
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-(length:--text-xs-size) font-medium">
                            {c.data.name}
                          </span>
                          {/*
                            역할 배지는 **서버 판정값(role)만** 본다(§8.5).
                            이름은 참가자가 자유롭게 정하므로 신뢰 신호가 될
                            수 없다 — "호스트" 라고 이름 지은 참가자와 진짜
                            호스트가 구분되지 않으면 사칭이 성립한다.
                          */}
                          {c.data.role === 'host' && (
                            <Badge variant="softSuccess" data-testid="chat-host-badge">
                              호스트
                            </Badge>
                          )}
                          <span className="text-(length:--text-xs-size) text-[var(--color-muted-foreground)]">
                            {clock(c.ts)}
                          </span>
                          {onDelete && (
                            <button
                              type="button"
                              aria-label="이 메시지 삭제"
                              title="이 메시지 삭제 (기록에 tombstone 이 남습니다)"
                              className="ms-auto opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                              onClick={() => onDelete(c)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                        <p className="text-(length:--text-sm-size) [overflow-wrap:anywhere]">
                          {c.data.body}
                        </p>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <form
            onSubmit={submit}
            className="flex flex-col gap-2 border-t border-[var(--color-border)] p-2"
          >
            {/* 거부 사유는 **입력 바로 위**에 둔다 — 고칠 곳 옆에 있어야
                고칠 수 있다. 문구는 서버가 준 것을 그대로 쓴다. */}
            {error && (
              <p
                role="alert"
                data-testid="chat-error"
                className="text-(length:--text-xs-size) text-[var(--color-destructive)]"
              >
                {error.message}
              </p>
            )}

            {/* 이름 칸은 **첫 전송 전에만** 보인다(§14.2 별도 단계 없음) */}
            {!named && (
              <Input
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, NAME_MAX))}
                placeholder="표시할 이름 (선택)"
                aria-label="표시할 이름"
                data-testid="chat-name"
                maxLength={NAME_MAX}
              />
            )}
            <div className="flex gap-2">
              <Input
                value={body}
                onChange={(e) => setBody(e.target.value.slice(0, BODY_MAX))}
                placeholder={disabled ? '회의가 종료되었습니다' : '메시지 입력'}
                aria-label="메시지"
                data-testid="chat-input"
                maxLength={BODY_MAX}
                disabled={disabled}
              />
              <Button
                type="submit"
                size="sm"
                aria-label="보내기"
                data-testid="chat-send"
                disabled={disabled || body.trim().length === 0}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </form>
        </section>
      )}
    </>
  )
}
